import { useEffect, useState } from "react";
import { ArrowUpRight, Mail, Phone, MapPin, Building2 } from "lucide-react";
import { useSiteContent } from "../context/SiteContentContext";
import { submitContact } from "../lib/adminApi";
import { sendContactEmails } from "../lib/email";
import { getSupabase, isSupabaseConfigured } from "../lib/supabase";

const EMPTY_FORM = {
  name: "",
  email: "",
  subject: "General enquiry",
  message: "",
};

export default function ContactPage() {
  const { org } = useSiteContent();
  const [form, setForm] = useState(EMPTY_FORM);
  const [toast, setToast] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 4500);
    return () => clearTimeout(timer);
  }, [toast]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const submitted = { ...form };
    try {
      let row = null;
      if (isSupabaseConfigured) {
        row = await submitContact(submitted);
      }
      try {
        await sendContactEmails({ ...submitted, adminEmail: org.email });
        if (row?.id && isSupabaseConfigured) {
          await getSupabase()?.rpc("mark_contact_emailed", { p_id: row.id });
        }
      } catch (emailErr) {
        console.warn("Email send failed:", emailErr.message);
      }
      setForm(EMPTY_FORM);
      setToast(
        `Thank you, ${submitted.name.split(" ")[0] || "friend"} — we've received your message and emailed a confirmation to your inbox.`
      );
    } catch (err) {
      setError(err.message || "Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {toast && (
        <div
          role="status"
          data-testid="contact-success"
          className="fixed top-24 left-1/2 z-[80] w-[min(92vw,32rem)] -translate-x-1/2 rounded-2xl bg-emerald-600 px-5 py-4 text-white shadow-[0_18px_40px_rgba(5,150,105,0.35)]"
        >
          {toast}
        </div>
      )}

      <section className="pt-28 sm:pt-32 md:pt-44 pb-8 md:pb-12">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-10 lg:px-14">
          <p className="font-mono uppercase text-xs tracking-[0.25em] text-[#EC4899]">Contact</p>
          <h1 className="font-display text-black text-[2.15rem] leading-[1.05] sm:text-5xl md:text-7xl lg:text-[100px] md:leading-[0.92] mt-5 sm:mt-6 max-w-4xl">
            We'd love to <span className="italic text-[#EC4899]">hear from you.</span>
          </h1>
          <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg text-black/70 leading-relaxed">
            Get in touch to find out more about our work, upcoming activities, or opportunities to get involved with Creative Flare Yorkshire C.I.C.
          </p>
        </div>
      </section>

      <section className="py-6 sm:py-10">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-6 lg:gap-10">
          {/* LEFT: details */}
          <div className="lg:col-span-5 space-y-4 sm:space-y-6 min-w-0">
            <div className="rounded-3xl p-5 sm:p-8 bg-[#FDF2F8]">
              <Building2 size={22} className="text-[#EC4899]" />
              <p className="font-mono uppercase text-xs tracking-[0.25em] text-[#EC4899] mt-4">Registered office</p>
              <p className="mt-3 font-display text-xl sm:text-2xl text-black leading-snug break-words">
                {org.registeredAddress.line1}<br />
                {org.registeredAddress.line2}
              </p>
              <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-black/50">Company No. {org.companyNumber}</p>
            </div>

            <div className="rounded-3xl p-5 sm:p-8 bg-[#0A0A0A] text-white">
              <MapPin size={22} className="text-[#EC4899]" />
              <p className="font-mono uppercase text-xs tracking-[0.25em] text-[#EC4899] mt-4">{org.venue.label}</p>
              <p className="mt-3 font-display text-xl sm:text-2xl leading-snug break-words">{org.venue.name}</p>
              <p className="mt-3 text-sm text-white/70">Where most of our workshops and community activities take place.</p>
            </div>

            <div className="space-y-3 sm:space-y-4 min-w-0">
              <a href={`mailto:${org.email}`} data-testid="contact-email" className="flex items-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl bg-white border border-black/6 hover:border-[#EC4899]/50 hover:shadow-ambient transition-all min-w-0">
                <span className="w-11 h-11 sm:w-12 sm:h-12 shrink-0 grid place-items-center rounded-full bg-[#EC4899]/12 text-[#EC4899]"><Mail size={20} /></span>
                <div className="min-w-0">
                  <div className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Email</div>
                  <div className="text-black font-medium break-all">{org.email}</div>
                </div>
              </a>
              {org.phones.map((phone, i) => (
                <a key={phone.tel} href={`tel:${phone.tel}`} data-testid={`contact-phone${i === 0 ? "" : `-${i + 1}`}`} className="flex items-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl bg-white border border-black/6 hover:border-[#EC4899]/50 hover:shadow-ambient transition-all min-w-0">
                  <span className="w-11 h-11 sm:w-12 sm:h-12 shrink-0 grid place-items-center rounded-full bg-[#EC4899]/12 text-[#EC4899]"><Phone size={20} /></span>
                  <div className="min-w-0">
                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Phone</div>
                    <div className="text-black font-medium break-words">{phone.display}</div>
                  </div>
                </a>
              ))}
            </div>

            <div>
              <p className="font-mono uppercase text-xs tracking-[0.25em] text-black/55 mb-3">Follow us</p>
              <div className="flex flex-wrap gap-3">
                {org.socials.map((s) => (
                  <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`contact-social-${s.name.toLowerCase()}`} className="px-4 py-2 rounded-full text-sm border border-black/15 text-black hover:bg-[#EC4899] hover:text-white hover:border-[#EC4899] transition-all">
                    {s.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: form */}
          <div className="lg:col-span-7 min-w-0">
            <div className="rounded-[24px] sm:rounded-[32px] bg-white border border-black/6 shadow-ambient p-5 sm:p-8 md:p-14">
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-black leading-tight">Send us a message</h2>
              <p className="text-black/65 mt-3 text-sm sm:text-base">We'll respond within two working days.</p>
              <form onSubmit={onSubmit} data-testid="contact-form" className="mt-6 sm:mt-8 space-y-5 sm:space-y-7">
                {error && <p className="text-sm text-[#BE185D]">{error}</p>}
                <div>
                  <label className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Your name</label>
                  <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="cf-input mt-2" />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Email</label>
                  <input data-testid="contact-form-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="cf-input mt-2" />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Subject</label>
                  <select data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="cf-input mt-2 bg-transparent">
                    <option>General enquiry</option>
                    <option>Programme / workshop</option>
                    <option>Volunteer with us</option>
                    <option>Partnership</option>
                    <option>Press</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Message</label>
                  <textarea data-testid="contact-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" className="cf-input mt-2 min-h-[140px]" />
                </div>
                <button data-testid="contact-submit" type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-full bg-[#EC4899] text-white font-medium hover:bg-[#DB2777] transition-all hover:-translate-y-[2px] shadow-pink disabled:opacity-50">
                  {submitting ? "Sending…" : "Send message"} <ArrowUpRight size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="py-12 sm:py-20">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-10 lg:px-14">
          <p className="font-mono uppercase text-xs tracking-[0.25em] text-[#EC4899]">Find us</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-black mt-3 break-words">
            {org.registeredAddress.line1}, {org.registeredAddress.line2}
          </h2>
          <div className="rounded-[24px] sm:rounded-[32px] overflow-hidden shadow-ambient mt-6 sm:mt-8">
            <iframe
              data-testid="contact-map"
              title={`Google Map — ${org.registeredAddress.line1}, ${org.registeredAddress.line2}`}
              src={`https://maps.google.com/maps?q=${encodeURIComponent(
                `${org.registeredAddress.line1}, ${org.registeredAddress.line2}`
              )}&z=17&hl=en&output=embed`}
              className="w-full h-[240px] sm:h-[320px] md:h-[420px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              `${org.registeredAddress.line1}, ${org.registeredAddress.line2}`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-sm text-[#EC4899] hover:text-[#DB2777]"
          >
            Open in Google Maps <ArrowUpRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}
